import salaryService from 'ApiService/Accountant/salaryService';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import { useState, useEffect, useContext } from 'react';
import toast from 'react-hot-toast';
import { MdOutlinePercent } from 'react-icons/md';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const UpdateSalaryModal = ({ employee, onClose, onSalaryUpdated }) => {
  const [directAmount, setDirectAmount] = useState('');
  const [percentageHike, setPercentageHike] = useState('');
  const [loading, setLoading] = useState(false);
  const [calculatedSalary, setCalculatedSalary] = useState(employee.salary);
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);
  const { state } = useContext(AuthContext);

  useEffect(() => {
    if (directAmount) {
      setCalculatedSalary(parseFloat(directAmount));
    } else if (percentageHike) {
      const hike = parseFloat(percentageHike) / 100;
      setCalculatedSalary(employee.salary + employee.salary * hike);
    }
  }, [directAmount, percentageHike, employee.salary]);

  const Confirmation = async () => {
    if (!directAmount && !percentageHike) {
      toast.error('Please provide either a direct amount or percentage hike.');
      return;
    }

    if (directAmount && percentageHike) {
      toast.error('Please provide only one InputField at a time.');
      return;
    }

    setIsModalOpenConfirmation(true);
  };

  const handleUpdate = async () => {
    const selectedBranch = state?.selectedBranch || '';
    setLoading(true);

    try {
      let newSalary = 0;
      if (directAmount) {
        newSalary = parseFloat(directAmount);
      } else if (percentageHike) {
        const hike = parseFloat(percentageHike) / 100;
        newSalary = employee.salary + employee.salary * hike;
      }

      const payload = {
        employeeId: employee._id,
        currentSalary: newSalary,
        selectedBranch,
      };

      const response = await salaryService.updateEmployeeSalaryDetails(payload);

      if (response.data.success) {
        toast.success('Salary updated successfully!');
        onSalaryUpdated();
        onClose();
        setIsModalOpenConfirmation(false);
      } else {
        toast.error(response.data.message || 'Failed to update salary.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDirectAmount('');
    setPercentageHike('');
    setCalculatedSalary(employee.salary);
  };

  return (
    <div className="bg-black fixed inset-0 z-20 flex items-center justify-center bg-opacity-50">
      <div className="w-96 rounded-lg bg-white p-6 shadow-xl dark:bg-navy-700">
        <button
          className="bg-transparent text-black float-right ml-auto border-0 p-1 text-3xl font-semibold leading-none outline-none focus:outline-none"
          onClick={onClose}
        >
          <span className="bg-transparent text-black block h-6 w-6 text-2xl outline-none focus:outline-none dark:text-white">
            ×
          </span>
        </button>
        <h2 className="mb-4 text-xl font-bold dark:text-white">
          Update Salary
        </h2>
        <p className="mb-2 text-gray-600">Employee: {employee.name}</p>
        <p className="mb-4 text-gray-600">Current Salary: ₹{employee.salary}</p>

        {/* Direct Amount InputField */}
        <div className="mb-4">
          <InputField
            label="Direct Amount"
            type="text"
            value={directAmount}
            onChange={(e) => {
              const value = e.target.value;

              if (/^\d*\.?\d*$/.test(value)) {
                setDirectAmount(value);
                setPercentageHike('');
              }
            }}
            className="w-full rounded-lg border p-2"
            placeholder="Enter new salary amount"
            min="0"
          />
        </div>

        <div className="relative mb-4">
          <InputField
            label="Percentage Hike"
            type="text"
            value={percentageHike}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                setPercentageHike(value);
                setDirectAmount(''); // Clear direct amount field
              }
            }}
            className="w-full rounded-lg border p-2 pr-10"
            placeholder="Enter percentage hike"
            min="0"
            max="100"
          />
          <span className="absolute right-4 top-[50px] mb-1 flex items-center text-xl text-navy-800 dark:text-white">
            <MdOutlinePercent />
          </span>
        </div>

        {/* Display calculated new salary */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-600">
            Calculated New Salary:
          </p>
          <p className="text-lg font-bold text-gray-800 dark:text-white">
            ₹{calculatedSalary.toFixed(2)}
          </p>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleReset}
            className="rounded-lg bg-gray-300 px-4 py-2 text-gray-700"
          >
            Reset
          </button>
          <button
            onClick={Confirmation}
            disabled={loading}
            className="rounded-lg bg-brand-500 px-4 py-2 text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => setIsModalOpenConfirmation(false)}
        onConfirm={handleUpdate}
        title="Confirm Salary Update"
        message={
          <div className="space-y-3 text-left">
            <p className="text-lg font-semibold text-navy-700 dark:text-white">
              Please confirm the following salary update:
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-gray-50 p-3 dark:bg-navy-900">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Employee:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {employee?.name || 'Selected Employee'}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Current Salary:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                ₹{employee.salary.toLocaleString() || '0'}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                New Salary:
              </span>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                ₹{calculatedSalary.toLocaleString()}
              </span>

              {directAmount && (
                <>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Direct Amount:
                  </span>
                  <span className="text-sm text-navy-700 dark:text-white">
                    ₹{parseFloat(directAmount).toLocaleString()}
                  </span>
                </>
              )}

              {percentageHike && (
                <>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Percentage Hike:
                  </span>
                  <span className="text-sm text-navy-700 dark:text-white">
                    {percentageHike}%
                  </span>
                </>
              )}

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Difference:
              </span>
              <span
                className={`text-sm ${
                  calculatedSalary > employee.salary
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                ₹{(calculatedSalary - employee.salary).toLocaleString()}
              </span>
            </div>
            <p className="mt-2 text-sm text-red-500">
              Please verify all details before confirming. This will update the
              salary record for the employee.
            </p>
          </div>
        }
        confirmText="Update Salary"
        cancelText="Cancel"
        icon="salary"
      />
    </div>
  );
};

export default UpdateSalaryModal;
